"use client";

import { useMemo } from "react";
import { Image } from "react-datocms";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";

import { IDatoGeneralMeta, IDatoPageSettings, IconType, LinkRecord } from "@types";

import { Icon } from "components/atoms/Icon/Icon";
import { TranslatedLink } from "components/atoms/TranslatedLink/TranslatedLink";
import { Container } from "components/organisms/Container/Container";
import { Spacer } from "components/organisms/Spacer/Spacer";

import { Doormat } from "./Doormat";
import s from "./Footer.module.scss";
import { CookieLink } from "./CookieLink";
import { Locale, LU_LOCALES } from "consts/locale";

interface Props {
  general: IDatoGeneralMeta;
  pageSettings?: IDatoPageSettings;
}

export const Footer = ({ general, pageSettings }: Props) => {
  const locale = useLocale() as Locale;
  const t = useTranslations("common");

  // If no page settings are provided (null), use the default settings
  const settings = pageSettings ?? { enableDoormat: true };

  const footerIntro = useMemo(() => {
    return general?.footerOverrides?.intro || general?.footerIntro || "";
  }, [general]);

  const footerVAT = useMemo(() => {
    return general?.footerOverrides?.vat || general?.vat || "";
  }, [general]);

  const extraLinks = useMemo(() => {
    return general?.footerOverrides?.extraLinks;
  }, [general]);

  const footerSocialLinks = useMemo(() => {
    return general.footerOverrides?.socialLinks ?? general?.socialLinks ?? [];
  }, [general]);

  return (
    <>
      {settings.enableDoormat && !LU_LOCALES.includes(locale) && (
        <Doormat doormatImage={general.doormatImage} general={general} />
      )}
      <footer className={s.Footer}>
        <Container size="l">
          {general?.footerOverrides?.logo ? (
            <div className={s.OfficeLogo}>
              <Image
                data={general.footerOverrides.logo.responsiveImage}
                className={s.OfficeLogoImage}
              />
            </div>
          ) : (
            <Link href="/" aria-label="Pia Group" className={s.Logo}>
              <Image data={general.logo.responsiveImage} />
            </Link>
          )}
          <div className={s.Inner}>
            <div className={s.Info}>
              {(general.footerOverrides?.secondaryLogo ||
                general.footerOverrides?.kmoRegistrationNumbers) && (
                <div className={s.KmoSection}>
                  {general.footerOverrides?.secondaryLogo && (
                    <Image
                      data={general.footerOverrides.secondaryLogo.responsiveImage}
                      className={s.SecondaryLogoImage}
                      objectPosition="top"
                      layout="responsive"
                      objectFit="contain"
                    />
                  )}
                  {general.footerOverrides?.kmoRegistrationNumbers && (
                    <div
                      className={s.KmoRegistrationNumbers}
                      dangerouslySetInnerHTML={{
                        __html: general.footerOverrides.kmoRegistrationNumbers,
                      }}
                    />
                  )}
                </div>
              )}
              <Spacer size="l">
                {footerIntro && (
                  <div className={s.Intro} dangerouslySetInnerHTML={{ __html: footerIntro }} />
                )}
                {footerVAT && (
                  <div className={s.InfoText}>
                    <h3>{t("vat")}</h3>
                    <p>{footerVAT}</p>
                  </div>
                )}
                {extraLinks && extraLinks.length > 0 && (
                  <div className={s.Menu}>
                    <h3>Links</h3>
                    <ul>
                      {extraLinks.map((footerLink) => (
                        <li key={footerLink.id}>
                          <TranslatedLink record={footerLink}>{footerLink.title}</TranslatedLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {footerSocialLinks.length > 0 && (
                  <ul className={s.SocialLinks}>
                    {footerSocialLinks.map((link) => (
                      <li key={link.id}>
                        <a href={link.url} aria-label={`PIA Group ${link.icon}`}>
                          <Icon name={link.icon as IconType} size="xl" color="white" />
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </Spacer>
            </div>
            {general.footerNav?.map((column) => (
              <div className={s.Menu} key={column.id || column.title}>
                <h3>{column.title}</h3>
                <ul>
                  {column.links.map((link) => (
                    <LinkItem key={link.id} link={link} general={general} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Container>
        {general.subfooterNav && general.subfooterNav.length > 0 && (
          <Container size="l">
            <ul className={s.SubFooter}>
              {general.subfooterNav?.map((link) => (
                <LinkItem key={link.id} link={link} general={general} />
              ))}
              <li className={s.LinkItem}>
                <CookieLink text={t("change-cookie-preferences")} />
              </li>
            </ul>
          </Container>
        )}
      </footer>
    </>
  );
};

export function LinkItem({
  link,
  general: { footerOverrides },
}: {
  link: LinkRecord;
  general: IDatoGeneralMeta;
}) {
  if (link.link?.__typename === "ExternalLinkRecord") {
    return (
      <li key={link.id} className={s.LinkItem}>
        <a href={link.link.url}>{link.linkText}</a>
      </li>
    );
  }

  const overridenLink = (() => {
    const { slug } = link.link.link;
    if (slug === "cookie-policy" || slug === "politique-en-matiere-de-cookies") {
      return footerOverrides?.cookiePolicy ?? link.link.link;
    }
    if (
      slug === "privacyverklaring" ||
      slug === "politique-de-vie-privee" ||
      slug === "privacy-policy"
    ) {
      return footerOverrides?.privacyPolicy ?? link.link.link;
    }
    if (
      slug === "algemene-voorwaarden" ||
      slug === "conditions-generales" ||
      slug === "terms-and-conditions"
    ) {
      return footerOverrides?.termsConditions ?? link.link.link;
    }
    return link.link.link;
  })();

  return (
    <li key={link.id} className={s.LinkItem}>
      <TranslatedLink record={overridenLink}>{link.linkText}</TranslatedLink>
    </li>
  );
}
